import { useMemo, useCallback, useState, useEffect } from "react";
import { Button } from "@aidkitorg/component-library";
import { DateRangeFilter } from "./DateRangeFilter";
import { EventTypeFilter } from "./EventTypeFilter";
import { UserFilter } from "./UserFilter";
import { useAuditLogs } from "../../../../Contexts/AuditLogsContext";
import { useLocalizedStrings } from "../../../../Localization";
import { FilterParams } from "../../types";
import type { AuditLogs } from '@aidkitorg/types/lib/survey';

export const AuditLogFilters: React.FC<{
  defaultTableColumns?: AuditLogs['defaultTableColumns']
}> = ({ defaultTableColumns }) => {
  const L = useLocalizedStrings();
  const {
    auditLogsFilters,
    onAuditLogsFiltersChange,
    filterOptions
  } = useAuditLogs();

  // Determine which filters to show based on config
  const showTimestampFilter = defaultTableColumns?.timestamp === 'visibleAndFilterable';
  const showEventTypeFilter = defaultTableColumns?.eventType === 'visibleAndFilterable';
  const showUserNameFilter = defaultTableColumns?.userName === 'visibleAndFilterable';

  // Local state for managing filters before applying them
  const [localFilters, setLocalFilters] = useState<FilterParams>({
    startDate: auditLogsFilters.startDate || "",
    endDate: auditLogsFilters.endDate || "",
    events: auditLogsFilters.events || [],
    userIds: auditLogsFilters.userIds || [],
  });

  // Update local filters when context filters change
  useEffect(() => {
    setLocalFilters({
      startDate: auditLogsFilters.startDate || "",
      endDate: auditLogsFilters.endDate || "",
      events: auditLogsFilters.events || [],
      userIds: auditLogsFilters.userIds || [],
    });
  }, [auditLogsFilters]);

  // Get available options from the API response
  const { availableEvents, availableUsers } = useMemo(() => {
    return {
      availableEvents: (filterOptions?.events || []).sort(),
      availableUsers: (filterOptions?.users || []).sort((a, b) => a.userName.localeCompare(b.userName)),
    };
  }, [filterOptions]);

  // Filter handlers
  const handleStartDateChange = useCallback((date: string) => {
    setLocalFilters({ ...localFilters, startDate: date });
  }, [localFilters]);

  const handleEndDateChange = useCallback((date: string) => {
    setLocalFilters({ ...localFilters, endDate: date });
  }, [localFilters]);

  const handleEventsChange = useCallback((events: string[]) => {
    setLocalFilters({ ...localFilters, events });
  }, [localFilters]);

  const handleUsersChange = useCallback((users: string[]) => {
    setLocalFilters({ ...localFilters, userIds: users });
  }, [localFilters]);

  // Apply filters function
  const applyFilters = useCallback(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(localFilters).filter(([_, value]) => 
        value !== "" && (Array.isArray(value) ? value.length > 0 : true)
      )
    );
    onAuditLogsFiltersChange(cleanFilters);
  }, [localFilters, onAuditLogsFiltersChange]);

  // Reset filters function
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      startDate: "",
      endDate: "",
      events: [],
      userIds: [],
    };
    setLocalFilters(emptyFilters);
    onAuditLogsFiltersChange({});
  }, [onAuditLogsFiltersChange]);

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showTimestampFilter && (
          <DateRangeFilter
            startDate={localFilters.startDate || ""}
            endDate={localFilters.endDate || ""}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        )}
        {showEventTypeFilter && (
          <EventTypeFilter
            selectedEvents={localFilters.events || []}
            availableEvents={availableEvents}
            onEventsChange={handleEventsChange}
          />
        )}
        {showUserNameFilter && (
          <UserFilter
            selectedUsers={localFilters.userIds || []}
            availableUsers={availableUsers}
            onUsersChange={handleUsersChange}
          />
        )}
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.audit_logs.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.audit_logs.apply_filters}</Button>
        </div>
      </div>
    </div>
  );
};